import { CommonModule } from "@angular/common";
import { Component, OnInit, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ENV, LoggerService } from '@bequeathed/ngx-app-core';
import {
    Advice, AdviceOption, AdviceService, AnalyticsEventName, AnalyticsService,
    Interview, InterviewService, MessageService,
    Question, Recommendation, RulesService,
    UserMetadata
} from '@bequeathed/ngx-services';
import { PageUtil } from '@bequeathed/ngx-utils';
import { AdviceControl } from '../../../core/ui/control/advice-control/advice-control.component';
import { AnalyticsUtils } from "../../../core/utils/analytics.utils";
import { QuestionUtils } from "../../../core/utils/question.utils";

const LOG: LoggerService = LoggerService.get('RecommendationsComponent')

/**
 * Service to communicate with the Interview Service to pull Advice requests and recommendations
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'app-advice',
    templateUrl: './recommendations.component.html',
    styleUrls: ['./recommendations.component.sass'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        AdviceControl
    ]
})
export class RecommendationsComponent implements OnInit {

    type = 'ADVICE'
    initiator: string | undefined
    initiatorType: string | undefined
    interviewId: number | undefined
    lawfirm: string | null | undefined
    loading= false
    saving = false
    errorSaving = false
    requirementsUnmet = false
    requirementsUnmetChecked = false
    DEBUG = ENV.config.get("logLevel") === "DEBUG"

    // @ts-expect-error required
    @ViewChildren('recommendation') recommendations: QueryList<AdviceControl>

    adviceOptionsConfig: AdviceOption[] = []
    advice: Advice | null = null

    constructor(
        private router: Router,
        private title: Title,
        protected interviewService: InterviewService,
        private rulesService: RulesService,
        private adviceService: AdviceService,
        protected messageService: MessageService,
        private analytics: AnalyticsService,
        private route: ActivatedRoute
    ) {
        this.title.setTitle('Recommendations')
    }

    ngOnInit(): void {
        this.loading = true
        this.requirementsUnmet = false
        this.requirementsUnmetChecked = false

        this.route.params.subscribe((params: any) => {
            this.type = params.type
            this.interviewId = params.id
        })
        this.setupAdviceInterview().then(() => {
            LOG.debug('init', 'Loaded Advice interview...')
            this.loading = false
        })

        PageUtil.setFocus('adviceRecommendationsHeader')
    }

    get messages(): MessageService {
        return this.messageService
    }

    getText(id: string): string {
        return QuestionUtils.getText('ADVICE', { id: id } as Question, this.messages) ||
            this.messages.getText(`ADVICE.${id}`,'')
    }

    async setupAdviceInterview(): Promise<void> {
        const interview = await this.interviewService.get('WILL', this.interviewId!)
        await this.loadAdviceRecommendations(interview!)

        const charityAttr = this.interviewService.willAttributes.get('charity')
        const lawfirmAttr = this.interviewService.willAttributes.get('lawfirm')

        if (charityAttr) {
            this.initiatorType = 'charity'
            this.initiator = charityAttr
            QuestionUtils.setUserMetadata({ charityInitiator: this.initiator } as UserMetadata)

        } else if (lawfirmAttr) {
            this.initiatorType = 'lawfirm'
            this.initiator = lawfirmAttr
            QuestionUtils.setUserMetadata({ lawfirmInitiator: this.initiator } as UserMetadata)
        }

        const optionNames: string[] = []
        this.adviceOptionsConfig.forEach(option => {
            optionNames.push(option!.id!)
        })

        this.advice = await this.adviceService.start(interview!.id!, optionNames)

    }

    async loadAdviceRecommendations(interview?: Interview) {
        try {
            const res = await this.rulesService.getAdviceRecommendations(
                interview!.qa,
                this.interviewService.willAttributes.get('lawfirm')!,
                this.interviewService.willAttributes!.get('charity')!,
                interview?.recordedVideo || false,
            )

            if (!res) {
                LOG.error('No response!')
            } else {
                this.adviceOptionsConfig = res
            }
        } catch (err) {
            setTimeout(() => this.loadAdviceRecommendations(interview), 3000)
            LOG.error(`Error retrieving Advice Recommendations! Retrying in 3 seconds...${err}`)
            return
        }
    }

    getRecommendation(name: string): Recommendation | undefined {
        return this.advice!.recommendations.find(rec => rec.name == name)
    }

    isLastOption(rec: any): boolean {
        return this.adviceOptionsConfig.length - 1 === this.adviceOptionsConfig.indexOf(rec)
    }

    async completeInterview(): Promise<void> {
        if (!this.recommendations.reduce((acc: boolean, cntl: AdviceControl) => !cntl.invalid && acc, true)) {
            LOG.debug('completeInterview', 'Not completing yet, some items don\'t have a decision yet')
            this.requirementsUnmet = true
            return
        }
        this.requirementsUnmet = false
        this.saving = true

        this.adviceOptionsConfig?.forEach(opt => {
            if (opt.autoAccept) {
                const rec = this.getRecommendation(opt.id!)
                if (rec) rec.status = 'ACCEPTED'
            }
        })

        if (!(await this.adviceService.saveAnswers(this.interviewId!, this.advice!))) {
            this.errorSaving = true
            this.saving = false
            return
        }

        const allRequirementsMet = await this.adviceService.complete(this.interviewId!, this.advice!.adviceId!)
        this.saving = false

        // Advice is complete either way, outcome recorded via the back-end
        this.analytics.trackFunnelStage(AnalyticsEventName.ADVICE_COMPLETE, {
            customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
            interview_id: this.interviewId ?? '',
            page: `/interview/ADVICE/${this.interviewId}/recommendations`
        })
        if (allRequirementsMet) {
            await this.router.navigate(['interview', 'WILL', this.interviewId, 'complete'])
        } else {
            await this.router.navigate(['interview', 'WILL', this.interviewId, 'cannot-complete'])
        }
        this.saving = false
    }
}
